(() => {
/**
 * Conservative, pure classification for collection-size navigation.
 * Unknown category is null; unknown department is []. No network, DOM, storage,
 * mutation, collection-context inference, or size-based demographic inference.
 */
const CATEGORY_ORDER = Object.freeze(['Tops', 'Bottoms', 'Outerwear', 'Footwear', 'Dresses']);
const DEPARTMENT_ORDER = Object.freeze(['mens', 'womens', 'boys', 'girls']);

// Preserved from draft33_sizes/assets/t2s-scoped-sizes.js, with canonical group
// names also accepted. Unknown taxonomy values must be deliberately mapped.
const CATEGORY_MAP = Object.freeze({
  Tops: Object.freeze(['Shirts', 'Tops', 'Sweaters']),
  Bottoms: Object.freeze(['Pants', 'Dress pants', 'Skorts', 'Shorts', 'Jeans', 'Skirts', 'Leggings', 'Tracksuits', 'Rompers', 'Overalls']),
  Outerwear: Object.freeze(['Jackets', 'Vests', 'Coats']),
  Footwear: Object.freeze(['Boots', 'Sneakers', 'Work shoes', 'Dress shoes', 'Loafers', 'Shoes', 'Boat shoes', 'Sandals', 'Clogs & mules', 'Moccasins']),
  Dresses: Object.freeze(['Dresses'])
});

function normalize(value) {
  return typeof value === 'string'
    ? value.normalize('NFKC').toLowerCase().replace(/[\u2018\u2019\u02bc]/gu, "'").trim().replace(/\s+/gu, ' ')
    : '';
}

const categoryLookup = new Map(Object.entries(CATEGORY_MAP).flatMap(([group, labels]) =>
  [group, ...labels].map(label => [normalize(label), group])
));

// Unicode-aware whole-word boundaries: women never matches men, and neither
// substring matching nor collection URL/title fallbacks are used.
function hasWord(text, pattern) {
  return new RegExp(`(?:^|[^\\p{L}\\p{N}_])(?:${pattern})(?=$|[^\\p{L}\\p{N}_])`, 'u').test(text);
}
const TITLE_DEPARTMENT_PATTERNS = Object.freeze({
  mens: "men(?:'s|s)?",
  womens: "women(?:'s|s)?",
  boys: "boy(?:s|'s)?",
  girls: "girl(?:s|'s)?"
});
const ADULT_WORDS = 'adults?';
// Juniors is intentionally absent: it is also a women's sizing/merchandising
// label. Numeric sizes, years, brands and product categories imply no age.
const CHILD_WORDS = "kids?|children(?:'s)?|child(?:'s)?|youths?|toddlers?|infants?|bab(?:y|ies)|preschool";

function readTags(tags) {
  const list = Array.isArray(tags) ? tags : typeof tags === 'string' ? tags.split(',') : [];
  return [...new Set(list.filter(tag => typeof tag === 'string').map(normalize).filter(Boolean))];
}
function ordered(values) {
  return DEPARTMENT_ORDER.filter(department => values.includes(department));
}
function sameSet(a, b) {
  return a.length === b.length && a.every(value => b.includes(value));
}
function ageOf(department) {
  return department === 'mens' || department === 'womens' ? 'adult' : 'child';
}

/** Returns one approved group, or null. Does not infer category from title. */
function classifyCategory(category) {
  return categoryLookup.get(normalize(category)) ?? null;
}

/**
 * Diagnostic variant. Reasons and evidence are suitable for catalog QA, not
 * customer copy. Department evidence is evaluated independently of category;
 * callers still require both a non-null category and nonempty departments.
 */
function explainProduct(product = {}) {
  const item = product && typeof product === 'object' ? product : {};
  const vendor = normalize(item.vendor);
  const title = vendor ? normalize(item.title).split(vendor).join(' ').trim() : normalize(item.title);
  const tags = readTags(item.tags);
  const titleDepartments = DEPARTMENT_ORDER.filter(department => hasWord(title, TITLE_DEPARTMENT_PATTERNS[department]));
  const tagDepartments = DEPARTMENT_ORDER.filter(department => tags.includes(department));
  const explicitUnisex = hasWord(title, 'unisex');
  // A negated or style-only use must not widen a product into two departments.
  const qualifiedUnisex = /(?:\b(?:not|non|no)\s*[- ]?\s*unisex\b|\bunisex\s*[- ]\s*(?:inspired|style)\b)/u.test(title);
  const unisex = explicitUnisex && !qualifiedUnisex;
  const allDepartments = ordered([...titleDepartments, ...tagDepartments]);
  const ages = [];
  if (hasWord(title, ADULT_WORDS) || allDepartments.some(department => ageOf(department) === 'adult')) ages.push('adult');
  if (hasWord(title, CHILD_WORDS) || tags.includes('kids') || allDepartments.some(department => ageOf(department) === 'child')) ages.push('child');

  let departments = [];
  let departmentReason;
  if (ages.length > 1) {
    departmentReason = 'conflicting-age-evidence';
  } else if (titleDepartments.length && tagDepartments.length && !sameSet(titleDepartments, tagDepartments)) {
    // Even with unisex wording, disagreement between explicitly named title
    // departments and exact catalog department tags requires catalog review.
    departmentReason = 'conflicting-title-tags';
  } else if (unisex && ages.length === 1) {
    departments = ages[0] === 'adult' ? ['mens', 'womens'] : ['boys', 'girls'];
    departmentReason = 'explicit-unisex-with-age';
  } else if (unisex) {
    departmentReason = 'unisex-age-unknown';
  } else if (allDepartments.length > 1) {
    departmentReason = 'multiple-departments-without-unisex';
  } else if (allDepartments.length === 1) {
    departments = [...allDepartments];
    departmentReason = titleDepartments.length && tagDepartments.length ? 'title-and-tags-agree' : titleDepartments.length ? 'explicit-title' : 'exact-tag';
  } else {
    departmentReason = ages[0] === 'child' ? 'kids-gender-unknown' : 'department-unknown';
  }

  const broadTags = CATEGORY_ORDER.filter(group => tags.includes(normalize(group)));
  const category = broadTags.length === 1 ? broadTags[0] : classifyCategory(item.category);
  if (category === 'Dresses') {
    const allowed = departments.filter(department => department === 'womens' || department === 'girls');
    if (allowed.length !== departments.length) {
      departments = allowed;
      departmentReason = allowed.length ? 'dresses-female-departments-only' : 'dresses-department-not-allowed';
    }
  }
  return {
    category,
    departments,
    categoryReason: category ? 'mapped-category' : 'category-unknown',
    departmentReason,
    evidence: {titleDepartments, tagDepartments, ages, explicitUnisex: unisex}
  };
}

/** Main integration API: pure, stable, serializable, and input-preserving. */
function classifyProduct(product) {
  const {category, departments, evidence} = explainProduct(product);
  const tags = readTags(product?.tags);
  const taggedCategories = CATEGORY_ORDER.filter(group => tags.includes(normalize(group)));
  return {category, departments, kids: evidence.ages.length === 1 && evidence.ages[0] === 'child', categories: taggedCategories.length ? taggedCategories : category ? [category] : []};
}

/** The two approved collection-page exceptions; all other pages use broad groups. */
const PAGE_GROUP_ORDER = Object.freeze({
  footwear: Object.freeze(['Work Boots', 'Western Boots', 'Dress Shoes', 'Sneakers']),
  'womens-footwear': Object.freeze(['Boots', 'Sneakers', 'Flats & Casual Shoes', 'Heels & Dress Shoes', 'Sandals'])
});

function pageEvidence(product) {
  const item = product && typeof product === 'object' ? product : {};
  const title = normalize(item.title);
  const tags = readTags(item.tags).map(tag => tag.replace(/-/gu, ' ').replace(/\s+/gu, ' '));
  const category = normalize(item.category);
  const tagged = (...values) => values.some(value => tags.includes(value));
  const workTags = tagged('work boots', 'work boot', 'mens work boots', 'womens work boots', 'workwear boots');
  const westernTags = tagged('western', 'western boots', 'western boot', 'cowboy boots', 'cowboy boot', 'roper boots', 'roper boot', 'mens western boots', 'womens western boots', 'kids cowboy boots');
  const boot = category === 'boots' || hasWord(title, 'boots?') || tagged('boots', 'boot') || workTags || westernTags;
  const sneaker = category === 'sneakers' || hasWord(title, 'sneakers?|trainers?|(?:running|tennis)[ -]+shoes?|athletic(?:[ -]+work)?[ -]+shoes?') || tagged('sneakers', 'sneaker', 'trainers', 'running shoes', 'athletic shoes', 'tennis shoes');
  const sandal = category === 'sandals' || hasWord(title, 'sandals?') || tagged('sandals', 'sandal');
  const loafer = category === 'loafers' || hasWord(title, 'loafers?') || tagged('loafers', 'loafer');
  const flat = hasWord(title, '(?:ballet[ -]+)?flats|flat[ -]+shoes?') || tagged('flats', 'flat shoes', 'ballet flats');
  const heel = hasWord(title, 'heels|high[ -]+heels?|pumps|stilettos?') || tagged('heels', 'high heels', 'pumps', 'stilettos');
  const explicitDress = category === 'dress shoes' || hasWord(title, 'dress[ -]+shoes?') || tagged('dress shoes', 'dress shoe');
  const oxfordDerby = hasWord(title, 'oxfords?|derb(?:y|ies|ys)') || tagged('oxfords', 'oxford', 'derby', 'derbys', 'derbies', 'oxfords derbys', 'oxfords derbies');
  const work = workTags || (boot && (hasWord(title, 'work(?:wear)?[ -]+boots?|safety|(?:steel|composite|comp|alloy)[ -]+toe') || tagged('steel toe', 'composite toe', 'comp toe', 'alloy toe', 'safety toe')));
  const western = westernTags || (boot && hasWord(title, 'western|cowboy|roper'));
  return {category, boot, sneaker, sandal, loafer, flat, heel, explicitDress, oxfordDerby, work, western};
}

/**
 * Returns a display group or null (leave uncertain products in the normal grid).
 * Group identity and department identity remain independent: the caller must
 * still filter by classifyProduct(product).departments for the selected page.
 * Only exact exception handles activate custom groups; never infer from a URL.
 */
function classifyPageGroup(product, collectionHandle) {
  const broadCategory = classifyProduct(product).category;
  if (!Object.prototype.hasOwnProperty.call(PAGE_GROUP_ORDER, collectionHandle)) return broadCategory;
  if (broadCategory !== 'Footwear') return null;
  const e = pageEvidence(product);
  const women = collectionHandle === 'womens-footwear';
  const item=product&&typeof product==='object'?product:{};
  const casualTitle=normalize(item.title);
  const casualTags=readTags(item.tags).map(tag=>tag.replace(/-/gu,' ').replace(/\s+/gu,' '));
  const casual=women&&(['boat shoes','moccasins','clogs & mules'].includes(e.category)||hasWord(casualTitle,'boat[ -]+shoes?|moccasins?(?![ -]+toe)|clogs?|mules?')||casualTags.some(tag=>['boat shoes','boat shoe','moccasins','moccasin','clogs','clog','mules','mule','clogs & mules'].includes(tag)));
  // These construction types are mutually exclusive for navigation. Hybrid or
  // contradictory data stays ungrouped instead of picking the first match.
  if ([e.boot, e.sneaker, e.sandal, casual].filter(Boolean).length > 1) return null;
  if (e.boot) {
    // Work shoes carrying the observed broad "work boots" tag are not boots.
    if (!['boots', 'shoes', 'footwear'].includes(e.category) || e.loafer || e.flat) return null;
    if (women) return 'Boots';
    const groups = [e.work ? 'Work Boots' : null, e.western ? 'Western Boots' : null].filter(Boolean);
    return groups.length === 1 ? groups[0] : null;
  }
  if (e.sneaker) {
    if (!['sneakers', 'shoes', 'footwear', 'work shoes'].includes(e.category) || e.loafer || e.flat || e.explicitDress) return null;
    // Oxford/derby can describe sneaker styling, so it does not override this
    // explicit construction (an observed catalog case is Oxford Sneakers).
    return 'Sneakers';
  }
  if (e.sandal) {
    if (!['sandals', 'shoes', 'footwear'].includes(e.category) || e.loafer || e.flat) return null;
    return women ? 'Sandals' : null;
  }
  if(women&&(e.loafer||e.flat||casual))return e.heel||e.explicitDress?null:'Flats & Casual Shoes';
  if (e.category === 'work shoes' || e.category === 'boat shoes' || e.category === 'moccasins' || e.category === 'clogs & mules') return null;
  if (e.explicitDress || e.oxfordDerby || (women && e.heel)) return women ? 'Heels & Dress Shoes' : 'Dress Shoes';
  return null;
}


const TOPS_STYLE_ORDER = Object.freeze({
  mens: Object.freeze(['T-Shirts','Polos','Button-Up Shirts','Sweaters & Knit Vests','Sweatshirts & Hoodies']),
  womens: Object.freeze(['T-Shirts','Blouses & Button-Ups','Polos','Sweaters & Cardigans','Sweatshirts & Hoodies'])
});
/** Precise garment evidence only; tanks and conflicts stay in All styles. */
function classifyTopsStyle(product, department) {
  if (!TOPS_STYLE_ORDER[department] || !classifyProduct(product).categories.includes('Tops')) return null;
  const title=normalize(product?.title), tags=readTags(product?.tags);
  const tagged=(...values)=>values.some(v=>tags.includes(v));
  const tank=hasWord(title,'tank(?:[ -]+tops?)?|camisoles?|cami')||tagged('tank','tanks','tank tops','camisoles');
  if(tank)return null;
  const polo=hasWord(title,'polos?')||tagged('polos','polo shirts');
  const tee=hasWord(title,'t[ -]?shirts?|tee(?:[ -]+shirts?)?')||tagged('t-shirts','t shirts','tees');
  const sweat=hasWord(title,'sweatshirts?|hoodies?|hooded|fleece')||tagged('sweatshirts','hoodies','fleece');
  const sweater=hasWord(title,'sweaters?|cardigans?|knit[ -]+vests?')||tagged('sweaters','cardigans','knit vests')||(hasWord(title,'wool')&&hasWord(title,'(?:quarter|half|full)[ -]+zip'));
  const button=hasWord(title,'button[ -]+(?:up|down|shirts?)|blouses?')||tagged('button-up shirts','button-down shirts','blouses');
  // Polo closures and fleece pullover wording must not create duplicate groups.
  if([polo,tee,sweat||sweater].filter(Boolean).length>1)return null;
  if(polo)return button?null:'Polos';
  if(tee)return button?null:'T-Shirts';
  if(sweat)return sweater&&!hasWord(title,'fleece')?null:'Sweatshirts & Hoodies';
  if(sweater)return department==='mens'?'Sweaters & Knit Vests':'Sweaters & Cardigans';
  if(button)return department==='mens'?'Button-Up Shirts':'Blouses & Button-Ups';
  return null;
}


const BOTTOMS_STYLE_ORDER = Object.freeze({
  mens: Object.freeze(['Jeans','Pants','Shorts']),
  womens: Object.freeze(['Jeans','Pants','Shorts','Skirts & Skorts','Leggings'])
});
/** Mutually exclusive construction evidence; sets and uncertain garments stay All. */
function classifyBottomsStyle(product, department) {
  if(!BOTTOMS_STYLE_ORDER[department]||!classifyProduct(product).categories.includes('Bottoms'))return null;
  const title=normalize(product?.title),category=normalize(product?.category),tags=readTags(product?.tags);
  const tagged=(...values)=>values.some(v=>tags.includes(v));
  if(['tracksuits','rompers','overalls'].includes(category)||hasWord(title,'tracksuits?|(?:two|2)[ -]+piece|rompers?|jumpsuits?|overalls?|dungarees|matching[ -]+set|sets?')||tagged('tracksuits','rompers','jumpsuits','overalls','matching set','two piece set','two-piece set','set','sets'))return null;
  const skirts=['skirts','skorts'].includes(category)||hasWord(title,'skirts?|skorts?')||tagged('skirts','skorts');
  const leggings=category==='leggings'||hasWord(title,'leggings?|jeggings?')||tagged('leggings','jeggings');
  const shorts=category==='shorts'||hasWord(title,'shorts|bermudas?')||tagged('shorts');
  const jeans=category==='jeans'||hasWord(title,'jeans')||tagged('jeans');
  const explicitPants=hasWord(title,'chinos?|cargo[ -]+pants|dress[ -]+pants|trousers?')||tagged('chinos','cargo pants','dress pants','trousers');
  if([skirts,leggings,shorts,jeans].filter(Boolean).length>1)return null;
  if(skirts)return department==='womens'&&!hasWord(title,'pants|jeans|leggings')?'Skirts & Skorts':null;
  if(leggings)return department==='womens'?'Leggings':null;
  if(shorts)return 'Shorts';
  if(jeans)return explicitPants?null:'Jeans';
  if(['pants','dress pants'].includes(category)||explicitPants||hasWord(title,'pants')||tagged('pants'))return 'Pants';
  return null;
}


const OUTERWEAR_STYLE_ORDER = Object.freeze({
  mens: Object.freeze(['Jackets & Coats','Vests']),
  womens: Object.freeze(['Jackets & Coats','Blazers','Vests'])
});
function classifyOuterwearStyle(product, department) {
  if(!OUTERWEAR_STYLE_ORDER[department]||!classifyProduct(product).categories.includes('Outerwear'))return null;
  const title=normalize(product?.title),category=normalize(product?.category),tags=readTags(product?.tags);
  const tagged=(...values)=>values.some(v=>tags.includes(v));
  if(hasWord(title,'sweaters?|cardigans?|knit(?:ted)?[ -]+vests?')||tagged('sweaters','sweater vests','knit vests','cardigans'))return null;
  const vest=category==='vests'||hasWord(title,'vests?|gilets?')||tagged('vests','outerwear vests','gilets');
  const blazer=category==='blazers'||hasWord(title,'blazers?')||tagged('blazers');
  const explicitJacket=hasWord(title,'jackets?|coats?|parkas?|windbreakers?')||tagged('jackets','coats','parkas','windbreakers');
  if(vest&&(blazer||explicitJacket))return null;
  if(vest)return 'Vests';
  if(blazer)return department==='womens'?'Blazers':'Jackets & Coats';
  if(['jackets','coats'].includes(category)||explicitJacket)return 'Jackets & Coats';
  return null;
}

window.T2SSizeClassifier={OUTERWEAR_STYLE_ORDER,classifyOuterwearStyle,BOTTOMS_STYLE_ORDER,classifyBottomsStyle,TOPS_STYLE_ORDER,classifyTopsStyle,classifyProduct,classifyPageGroup,CATEGORY_ORDER,PAGE_GROUP_ORDER};
})();
/**
 * Reconcile a size/category selection with native collection product cards.
 * The caller supplies the complete, ordered, positive-stock product metadata for
 * the current collection URL, including all active native filters. nativeIndex,
 * when present, is zero-based in that native candidate list (before stock/group
 * filtering). Only the current 24-card page is ever adopted into the live DOM.
 *
 * Integration: mark body.t2s-size-active before the native grid is painted; guard
 * PaginatedList's async mutations while this class or data-t2s-size-managed is
 * present. Pass localized text: loading, results ({count}), empty, error, retry,
 * previous, next, pagination and page ({page}, {pages}).
 */
(() => {
  if (window.T2SSizeResults) return;

  const PAGE_SIZE = 24;
  const CARD_SELECTOR = ':scope > [ref="cards[]"][data-product-id]';
  const fallback = {
    loading: 'Loading matching products…',
    results: '{count} items in stock',
    empty: 'No matching products are currently in stock.',
    error: 'Matching products could not be loaded. Please try again.',
    retry: 'Try again',
    previous: 'Previous',
    next: 'Next',
    pagination: 'Product pages',
    page: 'Page {page} of {pages}',
  };
  let currentRun;

  function message(text, key, values = {}) {
    const template = typeof text?.[key] === 'string' && text[key] ? text[key] : fallback[key];
    return template.replace(/\{(count|page|pages)\}/g, (match, name) => String(values[name] ?? match));
  }

  function locationKey(url) {
    const key = new URL(url);
    key.hash = '';
    key.searchParams.sort();
    return key.href;
  }

  function normalizeURL(value) {
    const url = new URL(value || window.location.href, window.location.href);
    if (url.origin !== window.location.origin || !['mens','womens','kids','boys','girls'].includes(url.searchParams.get('t2s_department')) && !url.searchParams.get('filter.p.m.t2s.size')) {
      throw new Error('Size results require a same-origin collection URL with an audience or size filter.');
    }
    return url;
  }

  function begin(url, signal) {
    currentRun?.controller.abort();
    const controller = new AbortController();
    const run = { controller, url, key: locationKey(url) };
    currentRun = run;
    const abort = () => controller.abort();
    if (signal?.aborted) abort();
    else signal?.addEventListener('abort', abort, { once: true });
    run.cleanup = () => signal?.removeEventListener('abort', abort);
    return run;
  }

  function assertCurrent(run, list) {
    if (currentRun !== run || run.controller.signal.aborted || !list.isConnected ||
        run.key !== locationKey(window.location.href)) {
      throw new DOMException('The size selection changed.', 'AbortError');
    }
  }

  function findContext() {
    const region = document.getElementById('ResultsList');
    const list = region?.closest('results-list[section-id]');
    if (!list || !region.firstElementChild) throw new Error('The native collection results region is missing.');
    return { list, region, host: region.firstElementChild };
  }

  function stopNativePagination({ list, host, region }) {
    const grid = host.querySelector('[ref="grid"]');
    if (grid && !grid.dataset.t2sNativeLastPage) {
      grid.dataset.t2sNativeLastPage = grid.dataset.lastPage || '1';
    }
    document.body.classList.add('t2s-size-active');
    list.dataset.t2sSizeManaged = 'true';
    list.dataset.t2sSizeState = 'loading';
    region.removeAttribute('data-t2s-ready');
    list.infinityScrollObserver?.disconnect();
    list.pages?.clear();
    for (const element of host.querySelectorAll('.pagination, [ref="viewMorePrevious"], [ref="viewMoreNext"], .main-collection-grid__empty')) {
      element.remove();
    }
    host.querySelector('[data-t2s-size-pagination]')?.remove();
    list.setAttribute('aria-busy', 'true');
  }

  function statusElement(host) {
    let status = host.querySelector('[data-t2s-size-status]');
    if (!status) {
      status = document.createElement('p');
      status.className = 't2s-size-results__status';
      status.dataset.t2sSizeStatus = '';
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      status.setAttribute('aria-atomic', 'true');
      host.prepend(status);
    }
    return status;
  }

  function setCount(list, content, count) {
    for (const header of document.querySelectorAll('.t2s-ch__count')) {
      // Preserve the merchant's suffix and spacing. An unverified/loading count
      // has no ready marker, so the owning size-navigation CSS keeps it hidden.
      delete header.dataset.t2sSizeCount;
      if (Number.isSafeInteger(count) && count >= 0 && /^\s*\d/.test(header.textContent)) {
        header.textContent = header.textContent.replace(/^(\s*)[0-9][0-9,.]*/, (_, space) => space + count);
        header.dataset.t2sSizeCount = 'ready';
      }
    }
    for (const wrapper of list.querySelectorAll('[data-testid="products-count"]')) {
      // The scoped status above owns announcements; responsive native counts are
      // visual copies, so they must not announce the same update a second time.
      for (const node of wrapper.querySelectorAll('[role="status"]')) node.removeAttribute('role');
      const target = wrapper.querySelector('span') || wrapper;
      target.textContent = content;
    }
    for (const form of list.querySelectorAll('facets-form-component')) {
      if (count === undefined) delete form.dataset.resultsCount;
      else form.dataset.resultsCount = String(count);
    }
  }

  function pageLink(url, page) {
    const destination = new URL(url);
    for (const key of ['page', 'section_id', 'sections', 'view']) destination.searchParams.delete(key);
    if (page === 1) destination.searchParams.delete('t2s_page');
    else destination.searchParams.set('t2s_page', String(page));
    destination.hash = 'ResultsList';
    return destination.pathname + destination.search + destination.hash;
  }

  function pagination(url, page, pages, text) {
    if (pages < 2) return null;
    const nav = document.createElement('nav');
    nav.className = 't2s-size-pagination';
    nav.dataset.t2sSizePagination = '';
    nav.setAttribute('aria-label', message(text, 'pagination'));
    const addLink = (label, target, rel) => {
      const link = document.createElement('a');
      link.textContent = message(text, label);
      link.href = pageLink(url, target);
      link.rel = rel;
      nav.append(link);
    };
    if (page > 1) addLink('previous', page - 1, 'prev');
    const indicator = document.createElement('span');
    indicator.textContent = message(text, 'page', { page, pages });
    indicator.setAttribute('aria-current', 'page');
    nav.append(indicator);
    if (page < pages) addLink('next', page + 1, 'next');
    return nav;
  }

  function showError(context, url, text) {
    const { list, host } = context;
    stopNativePagination(context);
    const grid = host.querySelector('[ref="grid"]');
    if (grid) {
      grid.hidden = true;
      grid.dataset.lastPage = '1';
      grid.replaceChildren();
    }
    const status = statusElement(host);
    status.textContent = message(text, 'error');
    const retry = document.createElement('a');
    retry.textContent = message(text, 'retry');
    retry.href = url.pathname + url.search + '#ResultsList';
    status.append(document.createTextNode(' '), retry);
    setCount(list, message(text, 'error'));
    list.dataset.t2sSizeState = 'error';
    list.setAttribute('aria-busy', 'false');
    context.region.setAttribute('data-t2s-ready', '');
  }

  /** Show an honest failure if the metadata request failed before render(). */
  function fail({ url = window.location.href, text = {} } = {}) {
    const selection = normalizeURL(url);
    if (locationKey(selection) !== locationKey(window.location.href)) return { status: 'aborted' };
    currentRun?.controller.abort();
    showError(findContext(), selection, text);
    return { status: 'error' };
  }

  async function render({ products, url = window.location.href, signal, text = {} }) {
    const selection = normalizeURL(url);
    const run = begin(selection, signal);
    let context;
    const requestedPages = [];
    try {
      context = findContext();
      const { list, host } = context;
      assertCurrent(run, list);
      const sectionId = list.getAttribute('section-id');
      let grid = host.querySelector('[ref="grid"]');
      let nativePageSize = Number(grid?.dataset.t2sNativePageSize);
      let lastPage = Number(grid?.dataset.t2sNativeLastPage || grid?.dataset.lastPage) || 1;
      let gridTemplate = grid?.cloneNode(false);
      let sectionStyles;
      const existingCards = grid ? Array.from(grid.querySelectorAll(CARD_SELECTOR)) : [];
      stopNativePagination(context);
      if (grid) {
        grid.hidden = true;
        grid.dataset.lastPage = '1';
        grid.replaceChildren();
      }
      const status = statusElement(host);
      status.textContent = message(text, 'loading');
      setCount(list, message(text, 'loading'));

      if (!Array.isArray(products)) throw new Error('The complete matching product list is required.');
      const seen = new Set();
      const ordered = products.map((product) => {
        const id = String(product?.id ?? '');
        if (!/^\d+$/.test(id) || seen.has(id)) throw new Error('The matching product list contains an invalid or duplicate ID.');
        seen.add(id);
        return { ...product, id };
      });
      const total = ordered.length;
      const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
      const requested = Number(selection.searchParams.get('t2s_page'));
      const page = Number.isSafeInteger(requested) && requested > 0 ? Math.min(requested, pages) : 1;
      const selected = ordered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
      const wanted = new Set(selected.map((product) => product.id));
      const cards = new Map();
      for (const card of existingCards) {
        if (wanted.has(card.dataset.productId)) cards.set(card.dataset.productId, card);
      }
      const fetched = new Set();

      const fetchPage = async (number) => {
        if (fetched.has(number)) return;
        assertCurrent(run, list);
        fetched.add(number);
        requestedPages.push(number);
        const endpoint = new URL(selection);
        for (const key of ['t2s_page', 'sections', 'view']) endpoint.searchParams.delete(key);
        endpoint.searchParams.set('section_id', sectionId);
        endpoint.searchParams.set('page', String(number));
        endpoint.hash = '';
        const response = await fetch(endpoint, { credentials: 'same-origin', signal: run.controller.signal });
        if (!response.ok) throw new Error(`The native product page returned HTTP ${response.status}.`);
        const html = await response.text();
        assertCurrent(run, list);
        // A detached template keeps every unselected image, iframe and script
        // inert. Only selected cards leave template.content and enter the page.
        const parsed = new DOMParser().parseFromString(
          `<!doctype html><html><body><template data-t2s-card-source>${html}</template></body></html>`,
          'text/html'
        );
        const fragment = parsed.querySelector('template[data-t2s-card-source]')?.content;
        const source = fragment && Array.from(fragment.querySelectorAll('results-list[section-id]'))
          .find((element) => element.getAttribute('section-id') === sectionId);
        const sourceGrid = source?.querySelector('[ref="grid"]');
        if (!sourceGrid) throw new Error('The native product grid is missing from the section response.');
        const responseLastPage = Number(sourceGrid.dataset.lastPage);
        if (!Number.isSafeInteger(responseLastPage) || responseLastPage < 1) throw new Error('Invalid native product pagination.');
        lastPage = responseLastPage;
        if (grid) grid.dataset.t2sNativeLastPage = String(lastPage);
        const sourcePageSize = Number(sourceGrid.dataset.t2sNativePageSize);
        if (Number.isSafeInteger(sourcePageSize) && sourcePageSize > 0) nativePageSize = sourcePageSize;
        if (!gridTemplate) gridTemplate = sourceGrid.cloneNode(false);
        const stylesheet = fragment.querySelector('style[data-section-stylesheet]');
        if (stylesheet) sectionStyles = stylesheet.textContent;
        for (const card of sourceGrid.querySelectorAll(CARD_SELECTOR)) {
          if (wanted.has(card.dataset.productId) && !cards.has(card.dataset.productId)) {
            cards.set(card.dataset.productId, card);
          }
        }
      };

      // Fetch directly by verified native indices when available. With no such
      // metadata, scan the size-filtered candidates one page at a time and stop
      // immediately when all current-page cards have been found.
      if (cards.size < wanted.size && Number.isSafeInteger(nativePageSize) && nativePageSize > 0) {
        const hints = new Set(selected.filter((product) => !cards.has(product.id) &&
          Number.isSafeInteger(product.nativeIndex) && product.nativeIndex >= 0)
          .map((product) => Math.floor(product.nativeIndex / nativePageSize) + 1));
        for (const number of [...hints].sort((a, b) => a - b)) {
          if (cards.size === wanted.size) break;
          await fetchPage(number);
        }
      }
      for (let number = 1; cards.size < wanted.size && number <= lastPage; number++) await fetchPage(number);
      assertCurrent(run, list);
      if (cards.size !== wanted.size) throw new Error('Some matching products changed or could not be loaded.');

      if (selected.length) {
        if (!grid) {
          grid = gridTemplate;
          if (!grid) throw new Error('The native product grid template is unavailable.');
          host.append(grid);
        }
        if (sectionStyles !== undefined) {
          const section = list.closest('[id^="shopify-section-"]') || list;
          let stylesheet = section.querySelector('style[data-section-stylesheet]');
          if (!stylesheet) {
            stylesheet = document.createElement('style');
            stylesheet.setAttribute('data-section-stylesheet', '');
            section.prepend(stylesheet);
          }
          stylesheet.textContent = sectionStyles;
        }
        const fragment = document.createDocumentFragment();
        for (const product of selected) fragment.append(cards.get(product.id));
        grid.replaceChildren(fragment);
        grid.dataset.t2sNativeLastPage = String(lastPage);
        grid.dataset.lastPage = '1';
        grid.hidden = false;
      }
      const count = message(text, 'results', { count: total });
      setCount(list, count, total);
      status.textContent = total ? count : `${count} ${message(text, 'empty')}`;
      const nav = pagination(selection, page, pages, text);
      if (nav) host.append(nav);
      list.dataset.t2sSizeState = total ? 'ready' : 'empty';
      list.setAttribute('aria-busy', 'false');
      context.region.setAttribute('data-t2s-ready', '');
      return { status: total ? 'ready' : 'empty', total, page, pages, rendered: selected.length, requestedPages };
    } catch (error) {
      if (error.name === 'AbortError' || currentRun !== run || run.controller.signal.aborted ||
          run.key !== locationKey(window.location.href) || (context && !context.list.isConnected)) {
        return { status: 'aborted' };
      }
      if (context) showError(context, selection, text);
      console.error('[T2S size results]', error);
      return { status: 'error', error, requestedPages };
    } finally {
      run.cleanup();
    }
  }

  window.T2SSizeResults = Object.freeze({ render, fail });
})();

(() => {
  if (customElements.get('t2s-scoped-sizes')) return;
  const { classifyProduct, classifyPageGroup, classifyOuterwearStyle, OUTERWEAR_STYLE_ORDER, classifyTopsStyle, TOPS_STYLE_ORDER, classifyBottomsStyle, BOTTOMS_STYLE_ORDER, CATEGORY_ORDER, PAGE_GROUP_ORDER } = window.T2SSizeClassifier;
  const SIZE = 'filter.p.m.t2s.size';
  const DEPTS = ['mens','womens','boys','girls'];
  const collator = new Intl.Collator(undefined, {numeric:true,sensitivity:'base'});
  const letters = ['XXS','XS','S','S/M','M','M/L','L','L/XL','XL','XXL','2XL','3XL','4XL','5XL'];
  const el = (tag, cls, text) => { const n=document.createElement(tag); if(cls)n.className=cls; if(text!==undefined)n.textContent=text; return n; };
  function clean(url) { const u=new URL(url); ['page','view','sections','section_id','t2s_page'].forEach(k=>u.searchParams.delete(k)); return u; }
  class ScopedSizes extends HTMLElement {
    connectedCallback() {
      this.events=new AbortController(); this.openGroups=new Map();
      this.text=JSON.parse(this.querySelector('[data-t2s-text]').textContent);
      document.addEventListener('shopify:collection:update',async event=>{try{await event.promise;}catch{} if(this.isConnected)this.load();},{signal:this.events.signal});
      window.addEventListener('popstate',()=>location.reload(),{signal:this.events.signal});
      window.addEventListener('pageshow',event=>{if(event.persisted)this.load();},{signal:this.events.signal});
      document.addEventListener('visibilitychange',()=>{if(document.visibilityState==='visible'&&Date.now()-this.loadedAt>60000)this.load();},{signal:this.events.signal});
      this.load();
    }
    async data(url,signal) {
      const fetchPage=async page=>{
        const u=clean(url);u.searchParams.set('view','t2s-size-data');u.searchParams.set('page',String(page));u.searchParams.set('preview_theme_id',this.dataset.theme);
        const r=await fetch(u,{credentials:'same-origin',signal,cache:'no-store'});if(!r.ok)throw new Error('Size data unavailable');
        const d=await r.json();if(String(d.theme)!==this.dataset.theme||d.collection!==this.dataset.collection||d.version!==2||d.page!==page||!Array.isArray(d.products))throw new Error('Unexpected size data');return d;
      };
      const first=await fetchPage(1),pages=first.pages,total=first.total,data=[first],products=[],ids=new Set();
      if(!Number.isInteger(pages)||pages<1||!Number.isInteger(total)||total<0||total>25000||pages!==Math.max(1,Math.ceil(total/250)))throw new Error('Invalid collection');
      for(let page=2;page<=pages;page+=3)data.push(...await Promise.all(Array.from({length:Math.min(3,pages-page+1)},(_,i)=>fetchPage(page+i))));
      for(const d of data){if(d.pages!==pages||d.total!==total)throw new Error('Collection changed');for(const p of d.products){if(ids.has(p.id))throw new Error('Duplicate page');ids.add(p.id);products.push({...p,nativeIndex:products.length,classification:classifyProduct(p),pageGroup:classifyPageGroup(p,this.dataset.collection),pageGroups:PAGE_GROUP_ORDER[this.dataset.collection]?[classifyPageGroup(p,this.dataset.collection)].filter(Boolean):classifyProduct(p).categories});}}
      if(products.length!==total)throw new Error('Incomplete collection');return products;
    }
    context(url) {
      const handle=this.dataset.collection;
      const path=url.pathname.split('/').filter(Boolean);const collectionIndex=path.indexOf('collections');const tags=(path[collectionIndex+2]||'').split('+');
      const explicit=DEPTS.filter(d=>tags.includes(d));
      if(explicit.length===1)return explicit[0];
      if(/^womens(?:-|$)/.test(handle))return 'womens';
      if(/^mens(?:-|$)/.test(handle)||handle==='footwear')return 'mens';
      if(/^boys(?:-|$)/.test(handle))return 'boys';
      if(/^girls(?:-|$)/.test(handle))return 'girls';
      if(/^kids(?:-|$)/.test(handle))return 'kids';
      return '';
    }
    link(url,text,cls){const a=el('a',cls,text);a.href=url.pathname+url.search;return a;}
    bindCategory(section,summary,current,dept,category,selected,active) {
      const key=dept+'|'+category;
      section.dataset.category=category;
      section.open=this.openGroups.has(key)?this.openGroups.get(key):Boolean(active&&selected);
      if(active&&selected)summary.setAttribute('aria-current','true');
      section.addEventListener('toggle',()=>this.openGroups.set(key,section.open),{signal:this.events.signal});
      summary.addEventListener('click',event=>{
        if(active&&selected)return;
        event.preventDefault();
        section.open=true;
        const destination=clean(current);
        destination.searchParams.delete(SIZE);
        destination.searchParams.set('t2s_department',dept);
        destination.searchParams.set('t2s_group',category);
        location.assign(destination.pathname+destination.search+destination.hash);
      },{signal:this.events.signal});
    }
    footwearPanel(products,current,dept,group,active) {
      const matches=products.filter(p=>p.stock===true&&p.classification.categories.includes('Footwear')&&typeof p.size==='string'&&p.size.trim()&&p.classification.departments.includes(dept));
      const footwearHandle=dept==='womens'?'womens-footwear':'footwear';
      const styleOrder=PAGE_GROUP_ORDER[footwearHandle];
      const dedicated=this.dataset.collection===footwearHandle;
      const footwearSelected=dedicated||group==='Footwear'||styleOrder.includes(group);
      const selectedSizes=footwearSelected?current.searchParams.getAll(SIZE).filter(size=>size.trim()):[];
      const scoped=products.filter(p=>p.stock===true&&p.classification.categories.includes('Footwear')&&p.classification.departments.includes(dept)&&(!selectedSizes.length||selectedSizes.includes(p.size))); 
      const fragment=document.createDocumentFragment();
      const section=el('details','t2s-scoped-sizes__group t2s-scoped-sizes__footwear-sizes'),summary=el('summary');
      const selection=(dedicated?'':this.text.Footwear+' · ')+(selectedSizes.length?`${this.dataset.size} ${selectedSizes.join(', ')}`:this.text.choose_size);
      summary.append(el('span','t2s-scoped-sizes__label',selection),el('span','t2s-scoped-sizes__count',selectedSizes.length?this.text.change_size:`${new Set(matches.map(p=>p.id)).size} ${this.dataset.stock}`));
      section.append(summary);this.bindCategory(section,summary,current,dept,'Footwear',footwearSelected,active);
      const sizes=new Map();for(const p of matches){if(!sizes.has(p.size))sizes.set(p.size,new Set());sizes.get(p.size).add(p.id);}
      const tiles=el('div','t2s-scoped-sizes__tiles');
      for(const [size,ids] of [...sizes].sort((a,b)=>collator.compare(a[0],b[0]))) {
        const selected=selectedSizes.includes(size),u=clean(current);u.searchParams.set('t2s_department',dept);
        if(selected){u.searchParams.delete(SIZE);if(dedicated)u.searchParams.delete('t2s_group');else u.searchParams.set('t2s_group', 'Footwear');}else{u.searchParams.set(SIZE,size);if(dedicated)u.searchParams.delete('t2s_group');else u.searchParams.set('t2s_group','Footwear');}
        const a=this.link(u,undefined,'t2s-scoped-sizes__tile');
        a.append(el('span','',size),el('span','',`${ids.size} ${this.dataset.stock}`));
        a.setAttribute('aria-label',`${this.text[dept]}, ${this.text.all_styles}, ${this.dataset.size} ${size}, ${ids.size} ${this.dataset.stock}${selected?', '+this.dataset.clear:''}`);
        if(selected)a.setAttribute('aria-current','page');tiles.append(a);
      }
      section.append(tiles);fragment.append(section);
      const styles=el('nav','t2s-scoped-sizes__styles');styles.setAttribute('aria-label',this.text.optional_style);
      styles.append(el('span','t2s-scoped-sizes__style-label',this.text.optional_style));
      for(const category of ['', ...(dept==='womens'?styleOrder:['Western Boots','Work Boots','Dress Shoes','Sneakers'])]) {
        const count=new Set(scoped.filter(p=>!category||classifyPageGroup(p,footwearHandle)===category).map(p=>p.id)).size;
        const selected=category===group||!category&&(footwearSelected||!group)&&!styleOrder.includes(group),u=clean(current);u.searchParams.set('t2s_department',dept);
        if(!footwearSelected)u.searchParams.delete(SIZE);
        if(category)u.searchParams.set('t2s_group',category);else if(dedicated)u.searchParams.delete('t2s_group');else u.searchParams.set('t2s_group','Footwear');
        const label=category?this.text[category]:this.text.all_styles;
        const a=(dept==='womens'&&!category)||count||(selected&&dept!=='womens')?this.link(u,`${label} (${count})`,'t2s-scoped-sizes__choice'):el('span','t2s-scoped-sizes__choice',`${label} (0)`);
        if(selected)a.setAttribute('aria-current','true');
        if(!count&&(!selected||dept==='womens')&&(dept!=='womens'||category))a.setAttribute('aria-disabled','true');
        styles.append(a);
      }
      if(footwearSelected&&selectedSizes.length)fragment.append(styles);
      if(dedicated)return fragment;
      const panel=el('div','t2s-scoped-sizes__footwear-panel');
      panel.append(fragment);
      return panel;
    }
    topsPanel(products,current,dept,group,active) {
      const matches=products.filter(p=>p.stock===true&&p.classification.categories.includes('Tops')&&typeof p.size==='string'&&p.size.trim()&&p.classification.departments.includes(dept));
      const styleOrder=TOPS_STYLE_ORDER[dept];
      const dedicated=false;
      const footwearSelected=dedicated||group==='Tops'||styleOrder.includes(group);
      const selectedSizes=footwearSelected?current.searchParams.getAll(SIZE).filter(size=>size.trim()):[];
      const scoped=products.filter(p=>p.stock===true&&p.classification.categories.includes('Tops')&&p.classification.departments.includes(dept)&&(!selectedSizes.length||selectedSizes.includes(p.size))); 
      const fragment=document.createDocumentFragment();
      const section=el('details','t2s-scoped-sizes__group t2s-scoped-sizes__footwear-sizes'),summary=el('summary');
      const selection=(dedicated?'':this.text.Tops+' · ')+(selectedSizes.length?`${this.dataset.size} ${selectedSizes.join(', ')}`:this.text.choose_size);
      summary.append(el('span','t2s-scoped-sizes__label',selection),el('span','t2s-scoped-sizes__count',selectedSizes.length?this.text.change_size:`${new Set(matches.map(p=>p.id)).size} ${this.dataset.stock}`));
      section.append(summary);this.bindCategory(section,summary,current,dept,'Tops',footwearSelected,active);
      const sizes=new Map();for(const p of matches){if(!sizes.has(p.size))sizes.set(p.size,new Set());sizes.get(p.size).add(p.id);}
      const tiles=el('div','t2s-scoped-sizes__tiles');
      for(const [size,ids] of [...sizes].sort((a,b)=>(letters.includes(a[0])&&letters.includes(b[0])?letters.indexOf(a[0])-letters.indexOf(b[0]):collator.compare(a[0],b[0])))) {
        const selected=selectedSizes.includes(size),u=clean(current);u.searchParams.set('t2s_department',dept);
        if(selected){u.searchParams.delete(SIZE);u.searchParams.set('t2s_group','Tops');}else{u.searchParams.set(SIZE,size);if(dedicated)u.searchParams.delete('t2s_group');else u.searchParams.set('t2s_group','Tops');}
        const a=this.link(u,undefined,'t2s-scoped-sizes__tile');
        a.append(el('span','',size),el('span','',`${ids.size} ${this.dataset.stock}`));
        a.setAttribute('aria-label',`${this.text[dept]}, ${this.text.all_styles}, ${this.dataset.size} ${size}, ${ids.size} ${this.dataset.stock}${selected?', '+this.dataset.clear:''}`);
        if(selected)a.setAttribute('aria-current','page');tiles.append(a);
      }
      section.append(tiles);fragment.append(section);
      const styles=el('nav','t2s-scoped-sizes__styles');styles.setAttribute('aria-label',this.text.optional_style);
      styles.append(el('span','t2s-scoped-sizes__style-label',this.text.optional_style));
      for(const category of ['', ...styleOrder.filter(style=>matches.some(p=>classifyTopsStyle(p,dept)===style))]) {
        const count=new Set(scoped.filter(p=>!category||classifyTopsStyle(p,dept)===category).map(p=>p.id)).size;
        const selected=category===group||!category&&(footwearSelected||!group)&&!styleOrder.includes(group),u=clean(current);u.searchParams.set('t2s_department',dept);
        if(!footwearSelected)u.searchParams.delete(SIZE);
        if(category)u.searchParams.set('t2s_group',category);else if(dedicated)u.searchParams.delete('t2s_group');else u.searchParams.set('t2s_group','Tops');
        const label=category?this.text[category]:this.text.all_styles;
        const a=!category||count?this.link(u,`${label} (${count})`,'t2s-scoped-sizes__choice'):el('span','t2s-scoped-sizes__choice',`${label} (0)`);
        if(selected)a.setAttribute('aria-current','true');
        if(!count&&category)a.setAttribute('aria-disabled','true');
        styles.append(a);
      }
      if(footwearSelected&&selectedSizes.length)fragment.append(styles);
      if(dedicated)return fragment;
      const panel=el('div','t2s-scoped-sizes__footwear-panel');
      panel.append(el('p','t2s-scoped-sizes__hint',this.text.tops_hint),fragment);
      return panel;
    }
    bottomsPanel(products,current,dept,group,active) {
      const matches=products.filter(p=>p.stock===true&&p.classification.categories.includes('Bottoms')&&typeof p.size==='string'&&p.size.trim()&&p.classification.departments.includes(dept));
      const styleOrder=BOTTOMS_STYLE_ORDER[dept];
      const dedicated=false;
      const footwearSelected=dedicated||group==='Bottoms'||styleOrder.includes(group);
      const selectedSizes=footwearSelected?current.searchParams.getAll(SIZE).filter(size=>size.trim()):[];
      const scoped=products.filter(p=>p.stock===true&&p.classification.categories.includes('Bottoms')&&p.classification.departments.includes(dept)&&(!selectedSizes.length||selectedSizes.includes(p.size))); 
      const fragment=document.createDocumentFragment();
      const section=el('details','t2s-scoped-sizes__group t2s-scoped-sizes__footwear-sizes'),summary=el('summary');
      const selection=(dedicated?'':this.text.Bottoms+' · ')+(selectedSizes.length?`${this.dataset.size} ${selectedSizes.join(', ')}`:this.text.choose_size);
      summary.append(el('span','t2s-scoped-sizes__label',selection),el('span','t2s-scoped-sizes__count',selectedSizes.length?this.text.change_size:`${new Set(matches.map(p=>p.id)).size} ${this.dataset.stock}`));
      section.append(summary);this.bindCategory(section,summary,current,dept,'Bottoms',footwearSelected,active);
      const sizes=new Map();for(const p of matches){if(!sizes.has(p.size))sizes.set(p.size,new Set());sizes.get(p.size).add(p.id);}
      const tiles=el('div','t2s-scoped-sizes__tiles');
      for(const [size,ids] of [...sizes].sort((a,b)=>(letters.includes(a[0])&&letters.includes(b[0])?letters.indexOf(a[0])-letters.indexOf(b[0]):collator.compare(a[0],b[0])))) {
        const selected=selectedSizes.includes(size),u=clean(current);u.searchParams.set('t2s_department',dept);
        if(selected){u.searchParams.delete(SIZE);u.searchParams.set('t2s_group','Bottoms');}else{u.searchParams.set(SIZE,size);if(dedicated)u.searchParams.delete('t2s_group');else u.searchParams.set('t2s_group','Bottoms');}
        const a=this.link(u,undefined,'t2s-scoped-sizes__tile');
        a.append(el('span','',size),el('span','',`${ids.size} ${this.dataset.stock}`));
        a.setAttribute('aria-label',`${this.text[dept]}, ${this.text.Bottoms}, ${this.text.all_styles}, ${this.dataset.size} ${size}, ${ids.size} ${this.dataset.stock}${selected?', '+this.dataset.clear:''}`);
        if(selected)a.setAttribute('aria-current','page');tiles.append(a);
      }
      section.append(tiles);fragment.append(section);
      const styles=el('nav','t2s-scoped-sizes__styles');styles.setAttribute('aria-label',this.text.optional_style);
      styles.append(el('span','t2s-scoped-sizes__style-label',this.text.optional_style));
      for(const category of ['', ...styleOrder.filter(style=>matches.some(p=>classifyBottomsStyle(p,dept)===style))]) {
        const count=new Set(scoped.filter(p=>!category||classifyBottomsStyle(p,dept)===category).map(p=>p.id)).size;
        const selected=category===group||!category&&(footwearSelected||!group)&&!styleOrder.includes(group),u=clean(current);u.searchParams.set('t2s_department',dept);
        if(!footwearSelected)u.searchParams.delete(SIZE);
        if(category)u.searchParams.set('t2s_group',category);else if(dedicated)u.searchParams.delete('t2s_group');else u.searchParams.set('t2s_group','Bottoms');
        const label=category?this.text[category]:this.text.all_styles;
        const a=!category||count?this.link(u,`${label} (${count})`,'t2s-scoped-sizes__choice'):el('span','t2s-scoped-sizes__choice',`${label} (0)`);
        if(selected)a.setAttribute('aria-current','true');
        if(!count&&category)a.setAttribute('aria-disabled','true');
        styles.append(a);
      }
      if(footwearSelected&&selectedSizes.length)fragment.append(styles);
      if(dedicated)return fragment;
      const panel=el('div','t2s-scoped-sizes__footwear-panel');
      panel.append(fragment);
      return panel;
    }
    outerwearPanel(products,current,dept,group,active) {
      const matches=products.filter(p=>p.stock===true&&p.classification.categories.includes('Outerwear')&&typeof p.size==='string'&&p.size.trim()&&p.classification.departments.includes(dept));
      const styleOrder=OUTERWEAR_STYLE_ORDER[dept];
      const dedicated=false;
      const footwearSelected=dedicated||group==='Outerwear'||styleOrder.includes(group);
      const selectedSizes=footwearSelected?current.searchParams.getAll(SIZE).filter(size=>size.trim()):[];
      const scoped=products.filter(p=>p.stock===true&&p.classification.categories.includes('Outerwear')&&p.classification.departments.includes(dept)&&(!selectedSizes.length||selectedSizes.includes(p.size))); 
      const fragment=document.createDocumentFragment();
      const section=el('details','t2s-scoped-sizes__group t2s-scoped-sizes__footwear-sizes'),summary=el('summary');
      const selection=(dedicated?'':this.text.Outerwear+' · ')+(selectedSizes.length?`${this.dataset.size} ${selectedSizes.join(', ')}`:this.text.choose_size);
      summary.append(el('span','t2s-scoped-sizes__label',selection),el('span','t2s-scoped-sizes__count',selectedSizes.length?this.text.change_size:`${new Set(matches.map(p=>p.id)).size} ${this.dataset.stock}`));
      section.append(summary);this.bindCategory(section,summary,current,dept,'Outerwear',footwearSelected,active);
      const sizes=new Map();for(const p of matches){if(!sizes.has(p.size))sizes.set(p.size,new Set());sizes.get(p.size).add(p.id);}
      const tiles=el('div','t2s-scoped-sizes__tiles');
      for(const [size,ids] of [...sizes].sort((a,b)=>(letters.includes(a[0])&&letters.includes(b[0])?letters.indexOf(a[0])-letters.indexOf(b[0]):collator.compare(a[0],b[0])))) {
        const selected=selectedSizes.includes(size),u=clean(current);u.searchParams.set('t2s_department',dept);
        if(selected){u.searchParams.delete(SIZE);u.searchParams.set('t2s_group','Outerwear');}else{u.searchParams.set(SIZE,size);if(dedicated)u.searchParams.delete('t2s_group');else u.searchParams.set('t2s_group','Outerwear');}
        const a=this.link(u,undefined,'t2s-scoped-sizes__tile');
        a.append(el('span','',size),el('span','',`${ids.size} ${this.dataset.stock}`));
        a.setAttribute('aria-label',`${this.text[dept]}, ${this.text.Outerwear}, ${this.text.all_styles}, ${this.dataset.size} ${size}, ${ids.size} ${this.dataset.stock}${selected?', '+this.dataset.clear:''}`);
        if(selected)a.setAttribute('aria-current','page');tiles.append(a);
      }
      section.append(tiles);fragment.append(section);
      const styles=el('nav','t2s-scoped-sizes__styles');styles.setAttribute('aria-label',this.text.optional_style);
      styles.append(el('span','t2s-scoped-sizes__style-label',this.text.optional_style));
      for(const category of ['', ...styleOrder.filter(style=>matches.some(p=>classifyOuterwearStyle(p,dept)===style))]) {
        const count=new Set(scoped.filter(p=>!category||classifyOuterwearStyle(p,dept)===category).map(p=>p.id)).size;
        const selected=category===group||!category&&(footwearSelected||!group)&&!styleOrder.includes(group),u=clean(current);u.searchParams.set('t2s_department',dept);
        if(!footwearSelected)u.searchParams.delete(SIZE);
        if(category)u.searchParams.set('t2s_group',category);else if(dedicated)u.searchParams.delete('t2s_group');else u.searchParams.set('t2s_group','Outerwear');
        const label=category?this.text[category]:this.text.all_styles;
        const a=!category||count?this.link(u,`${label} (${count})`,'t2s-scoped-sizes__choice'):el('span','t2s-scoped-sizes__choice',`${label} (0)`);
        if(selected)a.setAttribute('aria-current','true');
        if(!count&&category)a.setAttribute('aria-disabled','true');
        styles.append(a);
      }
      if(footwearSelected&&selectedSizes.length)fragment.append(styles);
      if(dedicated)return fragment;
      const panel=el('div','t2s-scoped-sizes__footwear-panel');
      panel.append(fragment);
      return panel;
    }
    async load() {
      this.abort?.abort(); const controller=this.abort=new AbortController(),signal=controller.signal;
      const target=this.querySelector('.t2s-scoped-sizes__groups'),status=this.querySelector('[role="status"]');
      target.replaceChildren();this.querySelector('.t2s-scoped-sizes__departments')?.remove();this.querySelector('.t2s-scoped-sizes__note')?.remove();status.textContent=this.dataset.loading;
      const current=new URL(location.href),context=this.context(current);
      const requested=current.searchParams.get('t2s_department');
      const dept=DEPTS.includes(context)?context:(DEPTS.includes(requested)?requested:'');
      const kidView=context==='kids'||requested==='kids'||['boys','girls'].includes(dept);
      const rawGroup=current.searchParams.get('t2s_group');
      let group=dept==='womens'&&rawGroup==='Flats & Loafers'?'Flats & Casual Shoes':rawGroup;
      const footwearHandle=dept==='womens'?'womens-footwear':'footwear';
      const styleOrder=PAGE_GROUP_ORDER[footwearHandle];
      const groupOrder=PAGE_GROUP_ORDER[this.dataset.collection]||CATEGORY_ORDER;
      const audience=dept||(requested==='kids'?'kids':'');
      const adultFootwear=['mens','womens'].includes(dept);
      if(adultFootwear&&!current.searchParams.getAll(SIZE).some(size=>size.trim())){
        const broad=styleOrder.includes(group)?'Footwear':TOPS_STYLE_ORDER[dept].includes(group)?'Tops':BOTTOMS_STYLE_ORDER[dept].includes(group)?'Bottoms':OUTERWEAR_STYLE_ORDER[dept].includes(group)?'Outerwear':null;
        if(broad){group=broad;current.searchParams.set('t2s_group',broad);history.replaceState(history.state,'',current.pathname+current.search+current.hash);}
      }
      const pilot=adultFootwear&&this.dataset.collection===footwearHandle;
      const footwearSelected=adultFootwear&&(pilot||group==='Footwear'||styleOrder.includes(group));
      const topsSelected=adultFootwear&&(group==='Tops'||TOPS_STYLE_ORDER[dept].includes(group));
      const bottomsSelected=adultFootwear&&(group==='Bottoms'||BOTTOMS_STYLE_ORDER[dept].includes(group));
      const outerwearSelected=adultFootwear&&(group==='Outerwear'||OUTERWEAR_STYLE_ORDER[dept].includes(group));
      this.removeAttribute('data-mens-footwear');
      this.toggleAttribute('data-footwear-pilot',pilot);
      const active=[...DEPTS,'kids'].includes(requested)||(pilot&&(current.searchParams.has(SIZE)||groupOrder.includes(group)));
      document.documentElement.classList.toggle('t2s-size-active',active);
      try {
        const products=await this.data(current,signal);if(signal.aborted)return;this.loadedAt=Date.now();
        const eligible=products.filter(p=>p.stock&&typeof p.size==='string'&&p.size.trim()&&(pilot||p.pageGroups.length)&&p.classification.departments.length);
        const omitted=products.filter(p=>p.stock&&!eligible.includes(p)).length;
        const available=d=>eligible.some(p=>p.classification.departments.includes(d));
        const nav=el('nav','t2s-scoped-sizes__departments');nav.setAttribute('aria-label',this.text.choose);
        const addChoice=(d,selected)=>{const u=clean(current);u.searchParams.delete(SIZE);u.searchParams.delete('t2s_group');u.searchParams.set('t2s_department',d);const a=this.link(u,this.text[d],'t2s-scoped-sizes__choice');if(selected)a.setAttribute('aria-current','true');nav.append(a);};
        if(!DEPTS.includes(context)) {
          if(context!=='kids'){addChoice('mens',dept==='mens');addChoice('womens',dept==='womens');addChoice('kids',kidView);}
          if(kidView){const sub=el('span','t2s-scoped-sizes__subchoices');nav.append(sub);for(const d of ['boys','girls']){const before=nav.lastChild;addChoice(d,dept===d);sub.append(nav.lastChild);}}
          target.before(nav);
        }
        let fragment=document.createDocumentFragment();
        let footwearChoices=products;
        if(dept&&current.searchParams.has(SIZE)){
          const allSizes=clean(current);allSizes.searchParams.delete(SIZE);
          footwearChoices=await this.data(allSizes,signal);if(signal.aborted)return;
        }
        if(pilot)fragment=this.footwearPanel(footwearChoices,current,dept,group,active);
        if(!pilot&&dept)for(const category of groupOrder) {
          if(category==='Tops'&&adultFootwear){
            if(footwearChoices.some(p=>p.stock===true&&p.classification.categories.includes('Tops')&&p.classification.departments.includes(dept)&&typeof p.size==='string'&&p.size.trim())){
              fragment.append(this.topsPanel(footwearChoices,current,dept,group,active));this.setAttribute('data-mens-footwear','');
            }
            continue;
          }
          if(category==='Bottoms'&&adultFootwear){
            if(footwearChoices.some(p=>p.stock===true&&p.classification.categories.includes('Bottoms')&&p.classification.departments.includes(dept)&&typeof p.size==='string'&&p.size.trim())){
              fragment.append(this.bottomsPanel(footwearChoices,current,dept,group,active));this.setAttribute('data-mens-footwear','');
            }
            continue;
          }
          if(category==='Outerwear'&&adultFootwear){
            if(footwearChoices.some(p=>p.stock===true&&p.classification.categories.includes('Outerwear')&&p.classification.departments.includes(dept)&&typeof p.size==='string'&&p.size.trim())){
              fragment.append(this.outerwearPanel(footwearChoices,current,dept,group,active));this.setAttribute('data-mens-footwear','');
            }
            continue;
          }
          if(category==='Footwear'&&adultFootwear){
            if(footwearChoices.some(p=>p.stock===true&&p.classification.categories.includes('Footwear')&&p.classification.departments.includes(dept)&&typeof p.size==='string'&&p.size.trim())){
              fragment.append(this.footwearPanel(footwearChoices,current,dept,group,active));this.setAttribute('data-mens-footwear','');
            }
            continue;
          }
          if(category==='Dresses'&&!['womens','girls'].includes(dept))continue;
          const matches=footwearChoices.filter(p=>p.stock&&typeof p.size==='string'&&p.size.trim()&&p.pageGroups.includes(category)&&p.classification.departments.includes(dept));
          if(!matches.length)continue;
          const sizes=new Map();for(const p of matches){if(!sizes.has(p.size))sizes.set(p.size,new Set());sizes.get(p.size).add(p.id);}
          const section=el('details','t2s-scoped-sizes__group'),summary=el('summary');
          const label=el('span','t2s-scoped-sizes__label',this.text[category]);const count=el('span','t2s-scoped-sizes__count',`${new Set(matches.map(p=>p.id)).size} ${this.dataset.stock}`);summary.append(label,count);section.append(summary);
          this.bindCategory(section,summary,current,dept,category,group===category,active);
          const tiles=el('div','t2s-scoped-sizes__tiles');
          for(const [size,ids] of [...sizes].sort((a,b)=>{const x=letters.indexOf(a[0]),y=letters.indexOf(b[0]);return x>=0&&y>=0?x-y:collator.compare(a[0],b[0]);})) {
            const selected=active&&group===category&&current.searchParams.getAll(SIZE).includes(size),u=clean(current);
            u.searchParams.set('t2s_department',dept);
            if(selected){u.searchParams.delete(SIZE);u.searchParams.set('t2s_group',category);}else{u.searchParams.set(SIZE,size);u.searchParams.set('t2s_group',category);}
            const a=this.link(u,undefined,'t2s-scoped-sizes__tile');a.append(el('span','',size),el('span','',`${ids.size} ${this.dataset.stock}`));a.setAttribute('aria-label',`${this.text[dept]}, ${this.text[category]}, ${this.dataset.size} ${size}, ${ids.size} ${this.dataset.stock}${selected?', '+this.dataset.clear:''}`);if(selected)a.setAttribute('aria-current','page');tiles.append(a);
          }
          section.append(tiles);fragment.append(section);
        }
        target.replaceChildren(fragment);
        // Unclassified items remain available in normal browsing without helper copy.
        status.textContent=dept?(target.children.length?'':this.dataset.empty):this.text.choose;
        if(active){const result=products.filter(p=>(outerwearSelected?p.stock===true&&p.classification.categories.includes('Outerwear'):bottomsSelected?p.stock===true&&p.classification.categories.includes('Bottoms'):topsSelected?p.stock===true&&p.classification.categories.includes('Tops'):footwearSelected?p.stock===true&&p.classification.categories.includes('Footwear'):p.stock)&&(!current.searchParams.has(SIZE)||current.searchParams.getAll(SIZE).includes(p.size))&&(audience==='kids'?p.classification.kids:p.classification.departments.includes(audience))&&(outerwearSelected?(!OUTERWEAR_STYLE_ORDER[dept].includes(group)||classifyOuterwearStyle(p,dept)===group):bottomsSelected?(!BOTTOMS_STYLE_ORDER[dept].includes(group)||classifyBottomsStyle(p,dept)===group):topsSelected?(!TOPS_STYLE_ORDER[dept].includes(group)||classifyTopsStyle(p,dept)===group):footwearSelected?(!styleOrder.includes(group)||classifyPageGroup(p,footwearHandle)===group):(!groupOrder.includes(group)||p.pageGroups.includes(group))));await window.T2SSizeResults.render({products:result,url:current,signal,text:this.text});}
      } catch(error) {
        if(signal.aborted)return;
        status.textContent=this.dataset.error;
        if(active){window.T2SSizeResults.fail({url:current,text:this.text});const restore=clean(current);restore.searchParams.delete(SIZE);restore.searchParams.delete('t2s_group');restore.searchParams.delete('t2s_department');const a=this.link(restore,this.text.restore,'t2s-scoped-sizes__browse');status.append(document.createTextNode(' '),a);}
      }
    }
    disconnectedCallback(){this.abort?.abort();this.events?.abort();}
  }
  customElements.define('t2s-scoped-sizes',ScopedSizes);
})();
